import React from 'react';
import { View, Text, Pressable, StyleSheet, Platform } from 'react-native';
import { useNavigation } from '../context/NavigationContext';
import { FL } from './floodlight/theme';
import { useUiStyle } from '../context/UiStyleContext';

const CURRENT_YEAR = new Date().getFullYear();

/** Floodlight routes (the original black ground with its glow): the footer floats over them in the Floodlight ink tokens. */
export const FLOODLIGHT_SCREENS = new Set([
  'Plans', 'Community', 'SameGameParlay', 'Why',
  'Login', 'Register', 'ForgotPassword', 'ResetPassword', 'AdminLogin',
  'Home', 'Dashboard', 'MainPage',
]);

/** Floodlight footer only when the page is actually drawn in Floodlight (Classic keeps its own values). */
export const usesPaperFooter = (screen, isClassic) => !isClassic && FLOODLIGHT_SCREENS.has(screen);

/** The beta landing (both looks) draws the footer inside its own page (`inline`), so the floating copy steps aside there. */
export const landingDrawsFooter = (screen) => screen === 'MainPage';

export default function LegalFooter({ inline = false }) {
  const { navigateTo, currentScreen } = useNavigation();
  const { isClassic } = useUiStyle();
  const paper = usesPaperFooter(currentScreen, isClassic);
  if (!inline && landingDrawsFooter(currentScreen)) return null;

  return (
    <View style={styles.footer}>
      <View style={styles.inner}>
        <Pressable
          onPress={() => navigateTo('Why')}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          style={({ hovered, pressed }) => [
            styles.linkPressable,
            Platform.OS === 'web' && hovered && (paper ? paperStyles.linkPressableHovered : styles.linkPressableHovered),
            pressed && styles.linkPressablePressed,
          ]}
        >
          {({ hovered }) => (
            <Text style={[styles.link, paper && paperStyles.link, Platform.OS === 'web' && hovered && styles.linkHovered, Platform.OS === 'web' && hovered && paper && paperStyles.linkHovered]}>
              Why BetOptimized
            </Text>
          )}
        </Pressable>
        <Text style={[styles.separator, paper && paperStyles.separator]}>|</Text>
        <Pressable
          onPress={() => navigateTo('Terms')}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          style={({ hovered, pressed }) => [
            styles.linkPressable,
            Platform.OS === 'web' && hovered && (paper ? paperStyles.linkPressableHovered : styles.linkPressableHovered),
            pressed && styles.linkPressablePressed,
          ]}
        >
          {({ hovered }) => (
            <Text style={[styles.link, paper && paperStyles.link, Platform.OS === 'web' && hovered && styles.linkHovered, Platform.OS === 'web' && hovered && paper && paperStyles.linkHovered]}>
              Terms of Service
            </Text>
          )}
        </Pressable>
        <Text style={[styles.separator, paper && paperStyles.separator]}>|</Text>
        <Pressable
          onPress={() => navigateTo('Privacy')}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          style={({ hovered, pressed }) => [
            styles.linkPressable,
            Platform.OS === 'web' && hovered && (paper ? paperStyles.linkPressableHovered : styles.linkPressableHovered),
            pressed && styles.linkPressablePressed,
          ]}
        >
          {({ hovered }) => (
            <Text style={[styles.link, paper && paperStyles.link, Platform.OS === 'web' && hovered && styles.linkHovered, Platform.OS === 'web' && hovered && paper && paperStyles.linkHovered]}>
              Privacy Policy
            </Text>
          )}
        </Pressable>
        <Text style={[styles.separator, paper && paperStyles.separator]}>|</Text>
        <Text style={[styles.copyright, paper && paperStyles.copyright]}>&copy; {CURRENT_YEAR} BetOptimized LLC</Text>
      </View>
      <Text style={[styles.rg, paper && paperStyles.copyright]}>
        21+ only. Gambling problem? Call{' '}
        <Text style={[styles.rgLink, paper && paperStyles.link]} accessibilityRole="link" {...(Platform.OS === 'web' ? { href: 'tel:18004262537' } : {})}>1-800-GAMBLER</Text>
        {' '}or the National Problem Gambling Helpline at{' '}
        <Text style={[styles.rgLink, paper && paperStyles.link]} accessibilityRole="link" {...(Platform.OS === 'web' ? { href: 'tel:18005224700' } : {})}>1-800-522-4700</Text>.
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  footer: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    backgroundColor: 'transparent',
  },
  inner: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  linkPressable: {
    backgroundColor: 'transparent',
    borderRadius: 4,
    paddingVertical: 2,
    paddingHorizontal: 4,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
  linkPressableHovered: {
    backgroundColor: 'rgba(255,255,255,0.06)',
  },
  linkPressablePressed: {
    opacity: 0.85,
  },
  link: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: 11,
    fontWeight: '500',
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
  linkHovered: {
    color: 'rgba(255,255,255,0.65)',
    ...(Platform.OS === 'web' ? { textDecorationLine: 'underline' } : {}),
  },
  separator: {
    color: 'rgba(255,255,255,0.15)',
    fontSize: 11,
  },
  copyright: {
    color: 'rgba(255,255,255,0.25)',
    fontSize: 11,
  },
  // Responsible-gambling line (launch audit): visible on every screen.
  rg: {
    marginTop: 4,
    textAlign: 'center',
    color: 'rgba(255,255,255,0.45)',
    fontSize: 11,
  },
  rgLink: {
    color: 'rgba(255,255,255,0.7)',
    textDecorationLine: 'underline',
  },
});

// Floodlight ink on the black ground (v1.71, dark only): links and copyright 65% white (7.4:1), hover full ink on a white wash.
const paperStyles = StyleSheet.create({
  linkPressableHovered: { backgroundColor: FL.inkWash },
  link: { color: FL.inkSoft },
  linkHovered: { color: FL.ink },
  separator: { color: FL.inkFaint },
  copyright: { color: FL.inkSoft },
});
